import {
    BaseButton,
    isTabletPortraitDown,
    PlaidAccountInfo,
    PlaidItemType,
    ProviderPlaidItemInfo,
    Spinner,
} from '@enter/core';
import { mdiArrowLeft, mdiBank, mdiDelete } from '@mdi/js';
import { Icon } from '@mdi/react';
import { inject, observer } from 'mobx-react';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { DialogContext } from 'services';
import { IObservedRootStore } from 'stores';
import { ConfirmDialog } from './components';
import * as B from './banking.styles';
import * as S from './shared.styles';

const formatSubtype = (subtype?: string): string | undefined => {
    if (!subtype) {
        return undefined;
    }
    return subtype
        .toLowerCase()
        .split('_')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
};

const formatMasks = (accounts: PlaidAccountInfo[]): string | undefined => {
    const masks = accounts.map(account => account.mask).filter((mask): mask is string => Boolean(mask));
    if (!masks.length) {
        return undefined;
    }
    return masks.map(mask => `•••• ${mask}`).join(', ');
};

const formatAccountMeta = (accounts: PlaidAccountInfo[]): string | undefined => {
    if (!accounts.length) {
        return undefined;
    }
    return accounts
        .map(account => {
            const subtype = formatSubtype(account.subtype);
            const parts = [account.name, subtype].filter(Boolean);
            return parts.join(' — ');
        })
        .join(' · ');
};

const institutionInitial = (name?: string): string => {
    const trimmed = (name || '').trim();
    return trimmed ? trimmed.charAt(0).toUpperCase() : '?';
};

export const Banking: React.FunctionComponent<IObservedRootStore> = inject('rootStore')(
    observer(
        (props: IObservedRootStore): React.ReactElement => {
            const { rootStore } = props;
            if (!rootStore) {
                return <></>;
            }
            const dialogContext = useContext(DialogContext);
            const [isDone, setDone] = useState(false);
            const [isLoading, setLoading] = useState(false);
            const [waitingFor, setWaitingFor] = useState<'add'>();
            const [removingId, setRemovingId] = useState<string>();
            const [loadFailed, setLoadFailed] = useState(false);
            const [linkToken, setLinkToken] = useState<string | null>(null);
            const [items, setItems] = useState<ProviderPlaidItemInfo[]>([]);

            useEffect(() => {
                if (isDone || isLoading) {
                    return;
                }
                (async () => {
                    setLoading(true);
                    try {
                        const nextItems = await rootStore.settingsStore.fetchPlaidBankingAccounts();
                        setItems(nextItems);
                        setLoadFailed(false);
                    } catch (err) {
                        dialogContext.showError(err);
                        setLoadFailed(true);
                    } finally {
                        setDone(true);
                        setLoading(false);
                    }
                })();
            }, [isDone, isLoading, rootStore, dialogContext]);

            const onSuccess = useCallback(
                async (publicToken: string, metadata: any) => {
                    try {
                        const institutionId = metadata?.institution?.institution_id;
                        await rootStore.settingsStore.connectPlaidAccount(publicToken, institutionId);
                        setDone(false);
                        setWaitingFor(undefined);
                    } catch (err) {
                        dialogContext.showError(err);
                        setWaitingFor(undefined);
                    }
                },
                [rootStore, dialogContext],
            );

            const onExit = useCallback(
                (err: any) => {
                    if (err) {
                        dialogContext.showError(err);
                    }
                    setWaitingFor(undefined);
                },
                [dialogContext],
            );

            const { open, ready } = usePlaidLink({
                token: linkToken,
                onSuccess,
                onExit,
            });

            const addAccount = async () => {
                setWaitingFor('add');
                try {
                    const token = await rootStore.settingsStore.getPlaidLinkToken();
                    if (token) {
                        setLinkToken(token);
                    } else {
                        setWaitingFor(undefined);
                    }
                } catch (err) {
                    dialogContext.showError(err);
                    setWaitingFor(undefined);
                }
            };

            useEffect(() => {
                if (linkToken && ready) {
                    open();
                }
            }, [linkToken, ready, open]);

            const removeItem = (item: ProviderPlaidItemInfo): void => {
                const id = item.plaidItemId;
                if (!id) {
                    return;
                }
                const institutionName = item.institutionName || 'this bank';
                const accountCount = item.accounts?.length || 0;
                const accountNote =
                    accountCount > 1
                        ? ` This will unlink all ${accountCount} accounts on the connection.`
                        : ' This will unlink the connected account.';

                const handleCancel = (): void => {
                    dialogContext.hideDialog?.();
                };

                const handleDelete = async (): Promise<void> => {
                    setRemovingId(id);
                    try {
                        await rootStore.settingsStore.deletePlaidItem(id);
                        dialogContext.hideDialog?.();
                        setItems(current => current.filter(currentItem => currentItem.plaidItemId !== id));
                        setDone(false);
                    } catch (err) {
                        dialogContext.showError(err);
                    } finally {
                        setRemovingId(undefined);
                    }
                };

                dialogContext.showDialog?.({
                    props: {
                        onClose: handleCancel,
                        disableBackdropClick: true,
                        disableEscapeKeyDown: true,
                        renderTitle: `Remove ${institutionName}?`,
                    },
                    Component: (
                        <ConfirmDialog
                            onSubmit={handleDelete}
                            onCancel={handleCancel}
                            yesMsg="Remove"
                            noMsg="Cancel"
                            message={`Removing the connection lets you reconnect through Plaid and select multiple accounts.${accountNote}`}
                        />
                    ),
                });
            };

            const renderRow = (item: ProviderPlaidItemInfo): React.ReactElement => {
                const accounts = item.accounts || [];
                const masks = formatMasks(accounts);
                const meta = formatAccountMeta(accounts);
                const canDelete = item.itemType === PlaidItemType.PLAID && Boolean(item.plaidItemId);
                const isRemoving = removingId === item.plaidItemId;

                return (
                    <B.AccountRow key={item.plaidItemId || item.externalItemId}>
                        <B.Avatar>{institutionInitial(item.institutionName)}</B.Avatar>
                        <B.AccountBody>
                            <B.AccountPrimary>
                                <B.InstitutionName>{item.institutionName || 'Unknown institution'}</B.InstitutionName>
                                {masks && <B.Masks>{masks}</B.Masks>}
                                <B.TypeChip>{item.itemType === PlaidItemType.MANUAL ? 'Manual' : 'Plaid'}</B.TypeChip>
                            </B.AccountPrimary>
                            {meta && <B.AccountMeta>{meta}</B.AccountMeta>}
                        </B.AccountBody>
                        <BaseButton
                            variant="fab-light"
                            size="large"
                            disabled={!canDelete || isRemoving}
                            aria-label={
                                canDelete
                                    ? `Remove ${item.institutionName || 'bank'} connection`
                                    : 'Manual banking items cannot be removed'
                            }
                            onClick={() => canDelete && removeItem(item)}>
                            {isRemoving ? <S.Waiting size="18px" /> : <Icon path={mdiDelete} size={1} />}
                        </BaseButton>
                    </B.AccountRow>
                );
            };

            return (
                <S.SubCategoryCol>
                    <S.SubCategoryItem>
                        <S.SubCategoryH1>
                            {isTabletPortraitDown() && (
                                <S.ReturnLink to="/settings">
                                    {(): React.ReactElement => <Icon path={mdiArrowLeft} size={1} />}
                                </S.ReturnLink>
                            )}
                            Banking
                        </S.SubCategoryH1>
                        <B.PageDescription>
                            Where payer deposits land and how remittances are posted.
                        </B.PageDescription>
                        <B.AccountsCard size="large">
                            <B.CardHeader>
                                <B.CardHeaderText>
                                    <B.CardTitle>Connected Bank Accounts</B.CardTitle>
                                    <B.CardSubtext>
                                        Linked through Plaid to reconcile carrier payments against your bank ledger.
                                    </B.CardSubtext>
                                </B.CardHeaderText>
                                <B.ConnectButton
                                    disabled={waitingFor === 'add'}
                                    variant="outlined"
                                    onClick={addAccount}>
                                    {waitingFor === 'add' ? (
                                        <S.Waiting size="18px" />
                                    ) : (
                                        <B.ConnectIcon>
                                            <Icon path={mdiBank} size={0.85} />
                                        </B.ConnectIcon>
                                    )}{' '}
                                    Connect account
                                </B.ConnectButton>
                            </B.CardHeader>
                            {isLoading && !items.length ? (
                                <B.LoadingState>
                                    <Spinner />
                                </B.LoadingState>
                            ) : loadFailed && !items.length ? (
                                <B.EmptyState>
                                    Bank connections could not be loaded. Refresh to try again.
                                </B.EmptyState>
                            ) : items.length ? (
                                <B.AccountList>{items.map(renderRow)}</B.AccountList>
                            ) : (
                                <B.EmptyState>
                                    No bank connections yet. Connect an account to post remittances against your
                                    ledger. Plaid lets you select multiple accounts in one session.
                                </B.EmptyState>
                            )}
                        </B.AccountsCard>
                    </S.SubCategoryItem>
                </S.SubCategoryCol>
            );
        },
    ),
);
